import { useMemo } from 'react';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData, ResolvableModeEnum } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ServiceUsagesTotalCardCmp_Total_Fragment } from '../../../components/ServiceUsages/TotalCard/Result';

import { ManualTotalForm_ResolvableServiceValue_Fragment } from '../fragments/manual-total-value.fragment';
import type { ManualTotalFormValues } from '../types/values.type';

export const buildManualTotalInitialValueFromAutomaticResult = ({
  defaultCurrencyId,
  defaultVatId,
  undiscountedAmountExcludingVat,
}: Readonly<{
  defaultCurrencyId: Id;
  defaultVatId: Id;
  undiscountedAmountExcludingVat:
    Readonly<{ currencyId: Id; value: number }> | undefined | null;
}>): ManualTotalFormValues['value'] => {
  const currencyId =
    undiscountedAmountExcludingVat?.currencyId ?? defaultCurrencyId;

  return {
    __typename: 'ResolvableServiceValue' as const,
    priceExcludingVat: {
      __typename: 'ResolvableAmount' as const,
      value: {
        __typename: 'ResolvableFloat' as const,
        mode: ResolvableModeEnum.Static,
        resolverId: null,
        value: undiscountedAmountExcludingVat?.value ?? 0,
      },
      currencyId: {
        __typename: 'ResolvableId' as const,
        mode: ResolvableModeEnum.Static,
        resolverId: null,
        value: currencyId,
      },
    },
    discountPercentage: {
      __typename: 'ResolvableFloat' as const,
      mode: ResolvableModeEnum.Static,
      resolverId: null,
      value: 0,
    },
    discountAmount: {
      __typename: 'ResolvableAmount' as const,
      value: {
        __typename: 'ResolvableFloat' as const,
        mode: ResolvableModeEnum.Static,
        resolverId: null,
        value: 0,
      },
      currencyId: {
        __typename: 'ResolvableId' as const,
        mode: ResolvableModeEnum.Static,
        resolverId: null,
        value: currencyId,
      },
    },
    vatId: {
      __typename: 'ResolvableId' as const,
      mode: ResolvableModeEnum.Static,
      resolverId: null,
      value: defaultVatId,
    },
  };
};

// The automatic result has no single VAT (its usages may mix rates), so the
// pre-fill falls back to the default VAT reference.
export const useManualTotalInitialValueFromAutomaticResult = (
  automaticResult:
    | FragmentType<typeof ServiceUsagesTotalCardCmp_Total_Fragment>
    | undefined
    | null,
) => {
  const referencesContext = useReferencesContext();

  const { defaultCurrency, defaultVat } = ensureDefined({
    defaultCurrency: referencesContext.defaultCurrency,
    defaultVat: referencesContext.defaultVat,
  });

  const result = getFragmentData(
    ServiceUsagesTotalCardCmp_Total_Fragment,
    automaticResult,
  );

  return useMemo(
    () =>
      buildManualTotalInitialValueFromAutomaticResult({
        defaultCurrencyId: defaultCurrency._id,
        defaultVatId: defaultVat._id,
        undiscountedAmountExcludingVat: result?.undiscountedAmountExcludingVat,
      }),
    [defaultCurrency._id, defaultVat._id, result],
  );
};

export const useManualTotalInitialValueFromManualTotal = (
  manualTotal:
    | FragmentType<typeof ManualTotalForm_ResolvableServiceValue_Fragment>
    | undefined
    | null,
): ManualTotalFormValues['value'] | undefined | null =>
  getFragmentData(ManualTotalForm_ResolvableServiceValue_Fragment, manualTotal);
